import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertBanner = ({ alerts, onAcknowledge, onDismiss }) => {
  const criticalAlerts = alerts?.filter(alert => alert?.severity === 'critical');
  const warningAlerts = alerts?.filter(alert => alert?.severity === 'warning');

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'bg-error/10 border-error text-error';
      case 'warning':
        return 'bg-warning/10 border-warning text-warning';
      default:
        return 'bg-muted border-border text-muted-foreground';
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return 'AlertOctagon';
      case 'warning':
        return 'AlertTriangle';
      default:
        return 'Info';
    }
  };

  if (alerts?.length === 0) {
    return (
      <div className="w-full bg-success/10 border-b-2 border-success px-4 md:px-6 lg:px-8 py-3 md:py-4">
        <div className="flex items-center justify-center gap-2 md:gap-3">
          <Icon name="CheckCircle2" size={20} color="var(--color-success)" />
          <span className="text-sm md:text-base font-medium text-success">
            All systems operational - No active alerts
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-card border-b-2 border-border">
      <div className="px-4 md:px-6 lg:px-8 py-3 md:py-4">
        <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 md:gap-4">
          <div className="flex items-center gap-2 md:gap-3">
            <Icon name="Bell" size={20} color="var(--color-error)" />
            <span className="text-sm md:text-base font-semibold text-foreground">
              Active Alerts: {criticalAlerts?.length} Critical, {warningAlerts?.length} Warning
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              iconName="CheckCheck"
              onClick={() => onAcknowledge('all')}
            >
              Acknowledge All
            </Button>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => onDismiss('all')}
            >
              Dismiss All
            </Button>
          </div>
        </div>

        <div className="mt-3 md:mt-4 space-y-2">
          {alerts?.slice(0, 3)?.map((alert) => (
            <div
              key={alert?.id}
              className={`flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 p-3 md:p-4 rounded-xl border-2 ${getSeverityColor(alert?.severity)}`}
            >
              <div className="flex items-start gap-2 md:gap-3 flex-1 min-w-0">
                <Icon
                  name={getSeverityIcon(alert?.severity)}
                  size={20}
                  color={alert?.severity === 'critical' ? 'var(--color-error)' : 'var(--color-warning)'}
                  className="flex-shrink-0 mt-0.5"
                />
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm md:text-base font-semibold text-foreground truncate">
                    {alert?.title}
                  </h4>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1 line-clamp-2">
                    {alert?.message}
                  </p>
                  <div className="flex flex-wrap items-center gap-2 md:gap-3 mt-2">
                    <span className="caption text-muted-foreground whitespace-nowrap">
                      {alert?.area}
                    </span>
                    <span className="caption text-muted-foreground whitespace-nowrap">
                      {alert?.timestamp}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Check"
                  onClick={() => onAcknowledge(alert?.id)}
                  className="flex-1 sm:flex-none"
                >
                  Acknowledge
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  onClick={() => onDismiss(alert?.id)}
                  className="flex-1 sm:flex-none"
                >
                  Dismiss
                </Button>
              </div>
            </div>
          ))}
        </div>

        {alerts?.length > 3 && (
          <div className="mt-3 md:mt-4 text-center">
            <Button variant="link" size="sm">
              View {alerts?.length - 3} more alerts
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertBanner;